import React, { useRef, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { FileUpload } from 'primereact/fileupload';
import { Image as PrimeImage } from 'primereact/image';
import { Toast } from 'primereact/toast';
import { Field } from 'formik';
import { Button } from 'primereact/button';
import { getStorage, ref, deleteObject } from 'firebase/storage';

export default function BasicInformationSection({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  shopTypes
}) {
  const toast = useRef(null);
  const [previews, setPreviews] = useState([]);

  // Sync previews with storeImages after deletions to prevent stale blobs from being displayed
  React.useEffect(() => {
    // Only keep previews that are still present in storeImages (by id)
    setPreviews(prev => prev.filter(p => (values.storeImages || []).some(img => typeof img === 'object' && img.id === p.id)));
  }, [values.storeImages]);
  
  const groupedItemTemplate = (option) => {
    return (
      <div className="flex align-items-center">
        <div>{option.label}</div>
      </div>
    );
  };

  const onUpload = (e) => {
    const files = Array.from(e.files);
    const currentImages = values.storeImages || [];
    const remainingSlots = 5 - currentImages.length;

    if (files.length > remainingSlots) {
      toast.current.show({ severity: 'warn', summary: 'Limit Reached', detail: `You can only upload ${remainingSlots} more images` });
      return;
    }

    // Prevent duplicate blobs by checking file name + size (as a simple heuristic)
    const existingFileKeys = new Set(
      currentImages
        .filter(img => typeof img === 'object' && img.file)
        .map(img => img.file && `${img.file.name}-${img.file.size}`)
    );

    const newPreviews = files.slice(0, remainingSlots)
      .filter(file => !existingFileKeys.has(`${file.name}-${file.size}`))
      .map(file => ({
        id: Date.now() + Math.random(),
        url: URL.createObjectURL(file),
        objectURL: URL.createObjectURL(file),
        file
      }));

    setPreviews(prev => [...prev, ...newPreviews]);

    // Always append new files to current storeImages
    const updatedImages = [
      ...currentImages,
      ...newPreviews
    ];

    setFieldValue('storeImages', updatedImages);
  };

  const removeImage = async (id) => {
    try {
      // Handle preview images (new uploads)
      const previewIndex = previews.findIndex(p => p.id === id);
      if (previewIndex >= 0) {
        const removedPreview = previews[previewIndex];
        URL.revokeObjectURL(removedPreview.url);

        // Remove the preview from previews
        const updatedPreviews = previews.filter(p => p.id !== id);
        setPreviews(updatedPreviews);

        // Remove the preview object from storeImages by id match (not by index)
        const updatedImages = (values.storeImages || []).filter(img => {
          // If img is a preview object, match by id
          if (typeof img === 'object' && img.id !== undefined) {
            return img.id !== id;
          }
          // If img is a string (saved image), always keep
          return true;
        });
        await setFieldValue('storeImages', updatedImages);
        return;
      }
      
      // Handle saved images (string URLs)
      const savedIndex = values.storeImages.findIndex(
        (img, index) => typeof img === 'string' && index === (id - previews.length)
      );
      
      if (savedIndex >= 0) {
        const imageUrl = values.storeImages[savedIndex];
        
        // Delete from Firebase Storage
        const storage = getStorage();
        const existingImagePath = decodeURIComponent(imageUrl.split('/o/')[1].split('?')[0]);
        await deleteObject(ref(storage, existingImagePath));
        
        // Update local state
        const updatedImages = values.storeImages.filter(
          (_, index) => index !== savedIndex
        );
        await setFieldValue('storeImages', updatedImages);
      }
    } catch (error) {
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to delete image',
        life: 3000
      });
    }
  };

  return (
    <div className="form-section">
      <Toast ref={toast} />
      <h3 className="section-title">Information</h3>

      <Field name="shopName">
        {({ field, meta }) => (
          <div className="field">
            <label htmlFor="shopName">Business Name</label>
            <InputText
              id="shopName"
              {...field}
              className={meta.touched && meta.error ? 'p-invalid' : ''}
            />
            {meta.touched && meta.error ? (
              <small className="p-error">{meta.error}</small>
            ) : null}
          </div>
        )}
      </Field>

      <Field name="shopType">
        {({ field, meta }) => (
          <div className="field">
            <label htmlFor="shopType">Category</label>
            <MultiSelect
              id="shopType"
              value={values.shopType || []}
              filter
              options={shopTypes}
              onChange={(e) => {
                setFieldValue('shopType', e.value);
              }}
              optionLabel="label"
              optionGroupLabel="label"
              optionGroupChildren="items"
              optionGroupTemplate={groupedItemTemplate}
              className={`w-full ${meta.touched && meta.error ? 'p-invalid' : ''}`}
              placeholder="Select categories"
              display="chip"
              maxSelectedLabels={3}
              selectedItemsLabel="{0} categories selected"
            />
            {meta.touched && meta.error ? (
              <small className="p-error">{meta.error}</small>
            ) : null}
          </div>
        )}
      </Field>

      <Field name="storeImages">
        {({ field }) => (
          <div className="field">
            <label>Storefront Images ({(values.storeImages || []).length}/5)</label>
            <FileUpload
              mode="basic"
              name={field.name}
              accept="image/*"
              auto
              maxFileSize={1000000}
              multiple
              onSelect={onUpload}
              chooseLabel="Upload Images"
              className="w-full"
              disabled={(values.storeImages || []).length >= 5}
            />
            
            <div className="flex flex-wrap gap-3 mt-3">
              {values.storeImages?.map((img, index) => {
                // Preview object
                if (typeof img === 'object' && img.id !== undefined) {
                  return (
                    <div key={img.id} className="relative w-full md:w-12rem h-12rem">
                      <div className="w-full h-full overflow-hidden">
                        <PrimeImage 
                          src={img.url} 
                          alt="Storefront preview" 
                          className="w-full h-full object-cover border-round shadow-2"
                          preview
                        />
                      </div>
                      <Button 
                        type="button"
                        icon="pi pi-trash" 
                        className="p-button-rounded p-button-danger p-button-sm absolute" 
                        style={{ top: '-10px', right: '-10px' }}
                        onClick={() => removeImage(img.id)}
                      />
                    </div>
                  );
                }
                // Saved string URL
                if (typeof img === 'string') {
                  return (
                    <div key={`saved-${index}`} className="relative w-full md:w-12rem h-12rem">
                      <div className="w-full h-full overflow-hidden">
                        <PrimeImage 
                          src={img} 
                          alt="Saved store image" 
                          className="border-round shadow-2"
                          preview
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </div>
                      <Button 
                        type="button"
                        icon="pi pi-trash" 
                        className="p-button-rounded p-button-danger p-button-sm absolute" 
                        style={{ top: '-10px', right: '-10px' }}
                        onClick={() => {
                          const updatedImages = values.storeImages.filter((_, i) => i !== index);
                          setFieldValue('storeImages', updatedImages);
                        }}
                      />
                    </div>
                  );
                }
                return null;
              })}
            </div>
          </div>
        )}
      </Field>
    </div>
  );
}